import { Card } from "@/components/ui/card";
import { Check, Circle, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

type StepState = "done" | "current" | "todo";
export interface MilestoneStep {
  key: string;
  label: string;
  state: StepState;
  caption?: string;
}

interface Props {
  steps: MilestoneStep[];
}

/** Horizontal on desktop, vertical on mobile. Gray / blue / green only. No red. */
export function LoanProgressStrip({ steps }: Props) {
  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold mb-3">Loan Milestones</h3>

      {/* Desktop horizontal */}
      <ol className="hidden md:flex items-center w-full">
        {steps.map((s, i) => {
          const last = i === steps.length - 1;
          return (
            <li key={s.key} className={cn("flex items-center", last ? "" : "flex-1")}>
              <StepDot state={s.state} />
              <div className="ml-2 mr-3 min-w-0">
                <div className={cn(
                  "text-xs font-medium leading-tight",
                  s.state === "todo" ? "text-muted-foreground" : "text-foreground",
                )}>{s.label}</div>
                {s.caption && (
                  <div className="text-[10px] text-muted-foreground truncate">{s.caption}</div>
                )}
              </div>
              {!last && (
                <div className={cn(
                  "flex-1 h-px",
                  steps[i + 1].state === "todo" && s.state !== "done"
                    ? "bg-border"
                    : "bg-[hsl(var(--status-active)/0.4)]",
                )} />
              )}
            </li>
          );
        })}
      </ol>

      {/* Mobile vertical */}
      <ol className="md:hidden space-y-3">
        {steps.map((s) => (
          <li key={s.key} className="flex items-start gap-3">
            <StepDot state={s.state} />
            <div className="min-w-0">
              <div className={cn(
                "text-sm font-medium",
                s.state === "todo" ? "text-muted-foreground" : "text-foreground",
              )}>{s.label}</div>
              {s.caption && <div className="text-xs text-muted-foreground">{s.caption}</div>}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function StepDot({ state }: { state: StepState }) {
  if (state === "done") {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--status-success)/0.15)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.4)] shrink-0">
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (state === "current") {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--status-active)/0.15)] text-[hsl(var(--status-active))] border border-[hsl(var(--status-active)/0.4)] shrink-0">
        <Clock className="h-3.5 w-3.5" />
      </span>
    );
  }
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground border border-border shrink-0">
      <Circle className="h-3 w-3" />
    </span>
  );
}